import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  Checkbox,
  RangeSlider,
  Badge,
  Divider,
  Box,
  Tooltip,
  Icon,
} from '@shopify/polaris';
import { QuestionCircleIcon } from '@shopify/polaris-icons';
import { SessionScoringSettings } from '../types';

interface Props {
  settings: SessionScoringSettings;
  onChange: (settings: SessionScoringSettings) => void;
}

export function SessionScoringForm({ settings, onChange }: Props) {
  const { t } = useTranslation();
  const update = useCallback(
    (patch: Partial<SessionScoringSettings>) => onChange({ ...settings, ...patch }),
    [settings, onChange]
  );

  const updateWeights = useCallback(
    (key: string, value: number) =>
      onChange({
        ...settings,
        weights: { ...settings.weights, [key]: value },
      }),
    [settings, onChange]
  );

  const updateThresholds = useCallback(
    (key: string, value: number) =>
      onChange({
        ...settings,
        thresholds: { ...settings.thresholds, [key]: value },
      }),
    [settings, onChange]
  );

  const exampleScore = 3 * settings.weights.productViews + Math.round(4 * settings.weights.timeOnSite * 10);
  const exampleIntent = exampleScore < settings.thresholds.low
    ? t('scoring.thresholds.cold')
    : exampleScore < settings.thresholds.medium
    ? t('scoring.thresholds.low')
    : exampleScore < settings.thresholds.high
    ? t('scoring.thresholds.medium')
    : t('scoring.thresholds.high');

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between">
            <Text variant="headingMd" as="h2">
              🎯 {t('scoring.title')}
            </Text>
            <Badge tone={settings.enabled ? 'success' : 'new'}>
              {settings.enabled ? t('common.active') : t('common.inactive')}
            </Badge>
          </InlineStack>
          <Text as="p" tone="subdued">
            {t('scoring.description')}
          </Text>
          <Checkbox
            label={t('scoring.enableLabel')}
            checked={settings.enabled}
            onChange={(v) => update({ enabled: v })}
            helpText={t('scoring.enableHelp')}
          />
        </BlockStack>
      </Card>

      {settings.enabled && (
        <>
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="200" blockAlign="center">
                <Text variant="headingMd" as="h2">{t('scoring.weights.title')}</Text>
                <Tooltip content={t('scoring.weights.titleHelp')}><Icon source={QuestionCircleIcon} tone="subdued" /></Tooltip>
              </InlineStack>
              <Text as="p" tone="subdued">
                {t('scoring.weights.description')}
              </Text>

              <Box paddingBlockStart="200">
                <BlockStack gap="400">
                  <RangeSlider
                    label={t('scoring.weights.productViewsLabel', { value: settings.weights.productViews })}
                    value={settings.weights.productViews}
                    min={0}
                    max={30}
                    step={1}
                    output
                    onChange={(v) => updateWeights('productViews', v as number)}
                    helpText={t('scoring.weights.productViewsHelp')}
                  />
                  <RangeSlider
                    label={t('scoring.weights.timeOnSiteLabel', { value: settings.weights.timeOnSite })}
                    value={settings.weights.timeOnSite * 10}
                    min={0}
                    max={20}
                    step={1}
                    output
                    onChange={(v) => updateWeights('timeOnSite', (v as number) / 10)}
                    helpText={t('scoring.weights.timeOnSiteHelp')}
                  />
                  <RangeSlider
                    label={t('scoring.weights.addToCartLabel', { value: settings.weights.addToCart })}
                    value={settings.weights.addToCart}
                    min={0}
                    max={50}
                    step={1}
                    output
                    onChange={(v) => updateWeights('addToCart', v as number)}
                    helpText={t('scoring.weights.addToCartHelp')}
                  />
                  <RangeSlider
                    label={t('scoring.weights.cartValueLabel', { value: settings.weights.cartValue })}
                    value={settings.weights.cartValue * 100}
                    min={0}
                    max={50}
                    step={1}
                    output
                    onChange={(v) => updateWeights('cartValue', (v as number) / 100)}
                    helpText={t('scoring.weights.cartValueHelp')}
                  />
                  <RangeSlider
                    label={t('scoring.weights.returnVisitLabel', { value: settings.weights.returnVisit })}
                    value={settings.weights.returnVisit}
                    min={0}
                    max={30}
                    step={1}
                    output
                    onChange={(v) => updateWeights('returnVisit', v as number)}
                    helpText={t('scoring.weights.returnVisitHelp')}
                  />
                  <RangeSlider
                    label={t('scoring.weights.scrollDepthLabel', { value: settings.weights.scrollDepth })}
                    value={settings.weights.scrollDepth * 100}
                    min={0}
                    max={50}
                    step={1}
                    output
                    onChange={(v) => updateWeights('scrollDepth', (v as number) / 100)}
                    helpText={t('scoring.weights.scrollDepthHelp')}
                  />
                </BlockStack>
              </Box>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">{t('scoring.thresholds.title')}</Text>
              <Text as="p" tone="subdued">
                {t('scoring.thresholds.description')}
              </Text>

              <TextField
                label={t('scoring.thresholds.lowLabel')}
                type="number"
                value={String(settings.thresholds.low)}
                onChange={(v) => updateThresholds('low', Number(v))}
                helpText={t('scoring.thresholds.lowHelp')}
                autoComplete="off"
              />
              <TextField
                label={t('scoring.thresholds.mediumLabel')}
                type="number"
                value={String(settings.thresholds.medium)}
                onChange={(v) => updateThresholds('medium', Number(v))}
                helpText={t('scoring.thresholds.mediumHelp')}
                autoComplete="off"
              />
              <TextField
                label={t('scoring.thresholds.highLabel')}
                type="number"
                value={String(settings.thresholds.high)}
                onChange={(v) => updateThresholds('high', Number(v))}
                helpText={t('scoring.thresholds.highHelp')}
                autoComplete="off"
              />

              <Divider />

              <Text variant="bodySm" as="p" tone="subdued">
                {t('scoring.thresholds.example', {
                  viewPts: 3 * settings.weights.productViews,
                  timePts: Math.round(4 * settings.weights.timeOnSite * 10),
                  score: exampleScore,
                  intent: exampleIntent,
                })}
              </Text>
            </BlockStack>
          </Card>
        </>
      )}
    </BlockStack>
  );
}
